import { memo } from "react";
import { Users, UserCheck, UserX, Info } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

interface SummaryStats {
  totalLeads: number;
  assignedLeads: number;
  unassignedLeads: number;
  activeAgents: number;
  totalAgents: number;
  avgResponseTime: string;
  conversionRate: string;
}

interface SummaryCardsProps {
  stats: SummaryStats;
}

export const SummaryCards = memo(function SummaryCards({ stats }: SummaryCardsProps) {
  const cards = [
    {
      title: "Total Leads",
      value: stats.totalLeads.toLocaleString(),
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
      tooltip: "Excludes \"Do Not Call\" leads and non-primary household members",
    },
    {
      title: "Assigned Leads",
      value: stats.assignedLeads.toLocaleString(),
      icon: UserCheck,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      title: "Unassigned Leads",
      value: stats.unassignedLeads.toLocaleString(),
      icon: UserX,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      title: "Assigned Agents",
      value: `${stats.activeAgents}/${stats.totalAgents}`,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
      tooltip: "Agents with at least one lead currently assigned to them, out of all agents in your agency",
      ring: stats.totalAgents > 0 ? stats.activeAgents / stats.totalAgents : 0,
    },
  ];

  return (
    <TooltipProvider>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title} className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
                  {card.title}
                  {card.tooltip && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[220px] text-xs">
                        {card.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </CardTitle>
                <div className={`p-2 rounded-lg ${card.bgColor}`}>
                  {typeof card.ring === "number" ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                      <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2.5" className="stroke-primary/20" />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        fill="none"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        className="stroke-primary"
                        strokeDasharray={`${Math.max(0, Math.min(1, card.ring)) * 2 * Math.PI * 6} ${2 * Math.PI * 6}`}
                        transform="rotate(-90 8 8)"
                      />
                    </svg>
                  ) : (
                    <Icon className={`h-4 w-4 ${card.color}`} />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{card.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </TooltipProvider>
  );
});
